/**
 * @licstart The following is the entire license notice for the
 * Javascript code in this page
 *
 * Copyright 2017 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * @licend The above is the entire license notice for the
 * Javascript code in this page
 */
export class OverlayManager {
    _overlays: object;
    _active: any;
    constructor() {
        this._overlays = {};
        this._active = null;
    }

    register = (name, element, callerCloseMethodArg: any = null, canForceCloseArg: any = null ) => {
        const callerCloseMethod = callerCloseMethodArg || null;
        const canForceClose = canForceCloseArg || false;

        return new Promise<void>((resolve) => {
            let container = void 0;
            if (!name || !element || !(container = element.parentNode)) {
                throw new Error('Not enough parameters.');
            } else if (this._overlays[name]) {
                throw new Error('The overlay is already registered.');
            }
            this._overlays[name] = {
                element: element,
                container: container,
                callerCloseMethod: callerCloseMethod,
                canForceClose: canForceClose
            };
            resolve();
        });
    };
    unregister = (name) => {
        return new Promise<void>((resolve) => {
            if (!this._overlays[name]) {
                throw new Error('The overlay does not exist.');
            } else if (this._active === name) {
                throw new Error('The overlay cannot be removed while it is active.');
            }
            delete this._overlays[name];
            resolve();
        });
    };
    open = (name) => {
        return new Promise<void>((resolve) => {
            if (!this._overlays[name]) {
                throw new Error('The overlay does not exist.');
            } else if (this._active) {
                if (this._overlays[name].canForceClose) {
                    this._closeThroughCaller();
                } else if (this._active === name) {
                    throw new Error('The overlay is already active.');
                } else {
                    throw new Error('Another overlay is currently active.');
                }
            }
            this._active = name;
            this._overlays[this._active].element.classList.remove('hidden');
            this._overlays[this._active].container.classList.remove('hidden');
            window.addEventListener('keydown', this._keyDownBound);
            resolve();
        });
    };
    close = (name) => {
        return new Promise<void>((resolve) => {
            if (!this._overlays[name]) {
                throw new Error('The overlay does not exist.');
            } else if (!this._active) {
                throw new Error('The overlay is currently not active.');
            } else if (this._active !== name) {
                throw new Error('Another overlay is currently active.');
            }
            this._overlays[this._active].container.classList.add('hidden');
            this._overlays[this._active].element.classList.add('hidden');
            this._active = null;
            window.removeEventListener('keydown', this._keyDownBound);
            resolve();
        });
    };
    keydown = function _keyDown(evt) {
        if (this._active && evt.keyCode === 27) {
            this._closeThroughCaller();
            evt.preventDefault();
        }
    };
    _closeThroughCaller = () => {
        if (this._overlays[this._active].callerCloseMethod) {
            this._overlays[this._active].callerCloseMethod();
        }
        if (this._active) {
            this.close(this._active);
        }
    };

    _keyDownBound = this.keydown;
}

const CLEANUP_TIMEOUT = 30000;
const RenderingStates = {
    INITIAL: 0,
    RUNNING: 1,
    PAUSED: 2,
    FINISHED: 3
};

export class PDFRenderingQueue {
    pdfViewer;
    pdfThumbnailViewer;
    onIdle;
    highestPriorityPage;
    idleTimeout;
    printing;
    isThumbnailViewEnabled;
    constructor() {
        this.pdfViewer = null;
        this.pdfThumbnailViewer = null;
        this.onIdle = null;
        this.highestPriorityPage = null;
        this.idleTimeout = null;
        this.printing = false;
        this.isThumbnailViewEnabled = false;
    }
    setViewer = (pdfViewer) => {
        this.pdfViewer = pdfViewer;
    };

    setThumbnailViewer = (pdfThumbnailViewer) => {
        this.pdfThumbnailViewer = pdfThumbnailViewer;
    }
    isHighestPriority = (view) => {
        return this.highestPriorityPage === view.renderingId;
    }
    renderHighestPriority = (currentlyVisiblePages?) => {
        if (this.idleTimeout) {
            clearTimeout(this.idleTimeout);
            this.idleTimeout = null;
        }
        if (this.pdfViewer.forceRendering(currentlyVisiblePages)) {
            return;
        }
        if (this.pdfThumbnailViewer && this.isThumbnailViewEnabled) {
            if (this.pdfThumbnailViewer.forceRendering()) {
                return;
            }
        }
        if (this.printing) {
            return;
        }
        if (this.onIdle) {
            this.idleTimeout = setTimeout(this.onIdle.bind(this), CLEANUP_TIMEOUT);
        }
    };
    getHighestPriority = (visible, views, scrolledDown) => {
        const visibleViews = visible.views;
        const numVisible = visibleViews.length;
        if (numVisible === 0) {
            return false;
        }
        for (let i = 0;i < numVisible;++i) {
            const view = visibleViews[i].view;
            if (!this.isViewFinished(view)) {
                return view;
            }
        }
        if (scrolledDown) {
            const nextPageIndex = visible.last.id;
            if (views[nextPageIndex] && !this.isViewFinished(views[nextPageIndex])) {
                return views[nextPageIndex];
            }
        } else {
            const previousPageIndex = visible.first.id - 2;
            if (views[previousPageIndex] && !this.isViewFinished(views[previousPageIndex])) {
                return views[previousPageIndex];
            }
        }
        return null;
    }
    isViewFinished = (view) => {
        return view.renderingState === RenderingStates.FINISHED;
    }
    renderView = (view) => {
        const _this = this;

        switch (view.renderingState) {
            case RenderingStates.FINISHED:
                return false;
            case RenderingStates.PAUSED:
                this.highestPriorityPage = view.renderingId;
                view.resume();
                break;
            case RenderingStates.RUNNING:
                this.highestPriorityPage = view.renderingId;
                break;
            case RenderingStates.INITIAL:
                this.highestPriorityPage = view.renderingId;
                const continueRendering = function continueRendering() {
                    _this.renderHighestPriority();
                };
                view.draw().then(continueRendering, continueRendering);
                break;
        }
        return true;
    }
};

function formatL10nValue(text, args) {
    if (!args) {
        return text;
    }
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, function (all, name) {
        return name in args ? args[name] : '{{' + name + '}}';
    });
}
export const _app = {
    PDFViewerApplication: {
        overlayManager: new OverlayManager()
    },
    PDFPrintServiceFactory: {
        instance: null
    }
}
const _ui_utils = {
    CSS_UNITS: (96.0 / 72.0),
    NullL10n: {
        getLanguage: function getLanguage() {
            return Promise.resolve('en-us');
        },
        getDirection: function getDirection() {
            return Promise.resolve('ltr');
        },
        get: function get(property, args, fallback) {
            return Promise.resolve(formatL10nValue(fallback, args));
        },
        translate: function translate(element) {
            return Promise.resolve();
        }
    }
}
let activeService = null;
export let overlayManager = null;
function renderPage(activeServiceOnEntry, pdfDocument, pageNumber, size) {
    const scratchCanvas = activeService.scratchCanvas;
    const PRINT_RESOLUTION = 250;
    const PRINT_UNITS = PRINT_RESOLUTION / 72.0;
    scratchCanvas.width = Math.floor(size.width * PRINT_UNITS);
    scratchCanvas.height = Math.floor(size.height * PRINT_UNITS);
    const width = Math.floor(size.width * _ui_utils.CSS_UNITS) + 'px';
    const height = Math.floor(size.height * _ui_utils.CSS_UNITS) + 'px';
    const ctx = scratchCanvas.getContext('2d');
    ctx.save();
    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.fillRect(0, 0, scratchCanvas.width, scratchCanvas.height);
    ctx.restore();
    return pdfDocument.getPage(pageNumber).then(function (pdfPage) {
        const renderContext = {
            canvasContext: ctx,
            transform: [PRINT_UNITS, 0, 0, PRINT_UNITS, 0, 0],
            viewport: pdfPage.getViewport({scale:1, rotation:size.rotation}),
            intent: 'print'
        };
        return pdfPage.render(renderContext).promise;
    }).then(function () {
        return {
            width: width,
            height: height
        };
    });
}
export class PDFPrintService{
    pdfDocument;
    pagesOverview;
    printContainer;
    l10n;
    disableCreateObjectURL;
    currentPage;
    scratchCanvas;
    pageStyleSheet;
    print;
    constructor(pdfDocument, pagesOverview, printContainer, l10n) {
        this.pdfDocument = pdfDocument;
        this.pagesOverview = pagesOverview;
        this.printContainer = printContainer;
        this.l10n = l10n || _ui_utils.NullL10n;
        this.disableCreateObjectURL = pdfDocument.loadingParams['disableCreateObjectURL'];
        this.currentPage = -1;
        this.scratchCanvas = document.createElement('canvas');
    }
    layout = () => {
        this.throwIfInactive();
        const body = document.querySelector('body');
        body.setAttribute('data-pdfjsprinting', 'true');
        const hasEqualPageSizes = this.pagesOverview.every(function (size) {
            return size.width === this.pagesOverview[0].width && size.height === this.pagesOverview[0].height;
        }, this);
        if (!hasEqualPageSizes) {
            console.warn('Not all pages have the same size. The printed ' + 'result may be incorrect!');
        }
        this.pageStyleSheet = document.createElement('style');
        const pageSize = this.pagesOverview[0];
        this.pageStyleSheet.textContent = '@supports ((size:A4) and (size:1pt 1pt)) {' + '@page { size: ' + pageSize.width + 'pt ' + pageSize.height + 'pt;}' + '}';
        body.appendChild(this.pageStyleSheet);
    };
    destroy = () => {
        if (activeService !== this) {
            return;
        }
        this.printContainer.textContent = '';
        if (this.pageStyleSheet) {
            this.pageStyleSheet.remove();
            this.pageStyleSheet = null;
        }
        this.scratchCanvas.width = this.scratchCanvas.height = 0;
        this.scratchCanvas = null;
        activeService = null;
        ensureOverlay().then(function () {
            if (overlayManager.active !== 'printServiceOverlay') {
                return;
            }
            overlayManager.close('printServiceOverlay');
        });
    };
    renderPages= () => {
        const pageCount = this.pagesOverview.length;
        const renderNextPage = (resolve, reject) => {
            this.throwIfInactive();
            if (++this.currentPage >= pageCount) {
                renderProgress(pageCount, pageCount, this.l10n);
                resolve();
                return;
            }
            const index = this.currentPage;
            renderProgress(index, pageCount, this.l10n);
            renderPage(this, this.pdfDocument, index + 1, this.pagesOverview[index]).then(this.useRenderedPage.bind(this)).then(function () {
                renderNextPage(resolve, reject);
            }, reject);
        };
        return new Promise(renderNextPage);
    };
    useRenderedPage = (printItem) => {
        this.throwIfInactive();
        const img = document.createElement('img');
        img.style.width = printItem.width;
        img.style.height = printItem.height;
        const scratchCanvas = this.scratchCanvas;
        if ('toBlob' in scratchCanvas && !this.disableCreateObjectURL) {
            scratchCanvas.toBlob(function (blob) {
                img.src = URL.createObjectURL(blob);
            });
        } else {
            img.src = scratchCanvas.toDataURL();
        }
        const wrapper = document.createElement('div');
        wrapper.appendChild(img);
        this.printContainer.appendChild(wrapper);
        return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
        });
    };
    performPrint = () => {
        this.throwIfInactive();
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                if (!this.active) {
                    resolve();
                    return;
                }
                this.print.call(window);
                setTimeout(resolve, 20);
            }, 0);
        });
    };
    setPrint = (printFn) => {
        this.print = printFn;
    };

    get active() {
        return this === activeService;
    };

    throwIfInactive = function throwIfInactive() {
        if (!this.active) {
            throw new Error('This print request was cancelled or completed.');
        }
    }
}

function dispatchEvent(eventType) {
    const event = document.createEvent('CustomEvent');
    event.initCustomEvent(eventType, false, false, 'custom');
    window.dispatchEvent(event);
}
function abort() {
    if (activeService) {
        activeService.destroy();
        dispatchEvent('afterprint');
    }
}
export function renderProgress(index, total, l10n) {
    const progressContainer = document.getElementById('printServiceOverlay');
    const progress = Math.round(100 * index / total);
    const progressBar = progressContainer.querySelector('progress');
    const progressPerc = progressContainer.querySelector('.relative-progress');
    progressBar.value = progress;
    l10n.get('print_progress_percent', { progress: progress }, progress + '%').then(function (msg) {
        progressPerc.textContent = msg;
    });
}
const hasAttachEvent = 'attachEvent' in document;
window.addEventListener('keydown', function (event) {
    if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey && (!event.shiftKey || (window as any).chrome || (window as any).opera)) {
        window.print();
        if (hasAttachEvent) {
            return;
        }
        event.preventDefault();
        if (event.stopImmediatePropagation) {
            event.stopImmediatePropagation();
        } else {
            event.stopPropagation();
        }
        return;
    }
}, true);
if (hasAttachEvent) {
    document.addEventListener('keydown', function (event : KeyboardEvent) {
        if (event.keyCode === 80 && event.ctrlKey) {
            // Do not attempt to assign to event.keyCode (it's read-only)
            return false;
        }
    });
}
if ('onbeforeprint' in window) {
    const stopPropagationIfNeeded = function stopPropagationIfNeeded(event) {
        if (event.detail !== 'custom' && event.stopImmediatePropagation) {
            event.stopImmediatePropagation();
        }
    };
    window.addEventListener('beforeprint', stopPropagationIfNeeded);
    window.addEventListener('afterprint', stopPropagationIfNeeded);
}


let overlayPromise = void 0;
export function ensureOverlay() {
    if (!overlayPromise) {
        overlayManager = _app.PDFViewerApplication.overlayManager;
        if (!overlayManager) {
            throw new Error('The overlay manager has not yet been initialized.');
        }
        overlayPromise = overlayManager.register('printServiceOverlay', document.getElementById('printServiceOverlay'), abort, true);
        document.getElementById('printCancel').onclick = abort;
    }
    return overlayPromise;
}
_app.PDFPrintServiceFactory.instance = {
    supportsPrinting: true,
    createPrintService: function createPrintService(pdfDocument, pagesOverview, printContainer, l10n) {
        if (activeService) {
            throw new Error('The print service is created and active.');
        }
        activeService = new PDFPrintService(pdfDocument, pagesOverview, printContainer, l10n);
        return activeService;
    }
};
